package com.trevorwiebe.timesheet.admin.reporting.presentation

import com.trevorwiebe.timesheet.admin.reporting.presentation.components.RevenuePerMileNotices
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.GeneralMileageResultsHeader
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.GeneralMileageTotalsRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.GeneralMileageTruckRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.IftaResultsHeader
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.IftaStateRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.IftaTotalsRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.LoadWithoutTruckRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.MissingStartEndBoundaryRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.MissingStartEndFixDialog
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.MissingStartEndResultsHeader
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.ReportingFilterRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.RevenuePerMileResultsHeader
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.RevenuePerMileTotalsRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.RevenuePerMileTruckRow
import com.trevorwiebe.timesheet.admin.reporting.presentation.components.SetTruckDialog
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.CollapsingHeaderLayout
import com.trevorwiebe.timesheet.core.presentation.common.FileExportIcon
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.botPadding
import com.trevorwiebe.timesheet.core.presentation.common.rememberCsvExporter
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun ReportingScreen(
    onBack: () -> Unit,
    viewModel: ReportingViewModel = koinViewModel()
) {
    val state by viewModel.state.collectAsState()
    val bottomPadding = botPadding()
    val exportCsv = rememberCsvExporter()

    val hasData = when (state.reportType) {
        ReportType.IFTA -> state.iftaRows.isNotEmpty()
        ReportType.GENERAL_MILEAGE -> state.generalMileageRows.isNotEmpty()
        ReportType.MISSING_START_END -> state.missingStartEndRows.isNotEmpty()
        ReportType.REVENUE_PER_MILE -> state.revenuePerMile.rows.isNotEmpty()
        ReportType.LOADS_WITHOUT_TRUCK -> state.loadsWithoutTruckRows.isNotEmpty()
    }
    val canExport = !state.isLoading && !state.isLoadReportLoading && state.error == null && hasData

    Scaffold(
        topBar = {
            TopBar(
                title = "Reporting",
                backIcon = { BackIcon() },
                onBack = onBack,
                actions = {
                    if (state.reportType != ReportType.MISSING_START_END) {
                        IconButton(
                            onClick = {
                                state.toCsvFile()?.let { exportCsv(it.fileName, it.content) }
                            },
                            enabled = canExport
                        ) {
                            Icon(
                                imageVector = FileExportIcon,
                                contentDescription = "Export to CSV",
                                tint = if (canExport) {
                                    MaterialTheme.colorScheme.tertiary
                                } else {
                                    MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                                }
                            )
                        }
                    }
                }
            )
        }
    ) { innerPadding ->
        ResponsiveContent(modifier = Modifier.padding(innerPadding)) {
        CollapsingHeaderLayout(
            header = {
                Column {
                    ReportingFilterRow(state = state, onEvent = viewModel::onEvent)
                    if (state.reportType == ReportType.REVENUE_PER_MILE && !state.isLoadReportLoading) {
                        RevenuePerMileNotices(
                            report = state.revenuePerMile,
                            onReviewUnassigned = {
                                viewModel.onEvent(ReportingEvent.OnSelectReportType(ReportType.LOADS_WITHOUT_TRUCK))
                            },
                        )
                    }
                    HorizontalDivider()
                }
            },
            pinnedHeader = {
                // Loads Without Truck is a plain list with no column headings.
                if (state.reportType != ReportType.LOADS_WITHOUT_TRUCK) Column {
                    when (state.reportType) {
                        ReportType.IFTA -> IftaResultsHeader()
                        ReportType.GENERAL_MILEAGE -> GeneralMileageResultsHeader()
                        ReportType.MISSING_START_END -> MissingStartEndResultsHeader()
                        ReportType.REVENUE_PER_MILE -> RevenuePerMileResultsHeader()
                        ReportType.LOADS_WITHOUT_TRUCK -> Unit
                    }
                    HorizontalDivider()
                }
            }
        ) { contentPadding ->
            when {
                state.isLoading || state.isLoadReportLoading -> Box(modifier = Modifier.fillMaxSize()) {
                    CircularProgressIndicator(
                        modifier = Modifier.align(Alignment.Center),
                        color = MaterialTheme.colorScheme.tertiary
                    )
                }
                state.error != null -> Box(modifier = Modifier.fillMaxSize()) {
                    Text(
                        text = state.error ?: "",
                        modifier = Modifier.align(Alignment.Center),
                        color = MaterialTheme.colorScheme.error
                    )
                }
                state.reportType == ReportType.IFTA -> {
                    if (state.iftaRows.isEmpty()) {
                        Box(modifier = Modifier.fillMaxSize()) {
                            Text(
                                text = "No IFTA data for the current filters.",
                                modifier = Modifier.align(Alignment.Center)
                            )
                        }
                    } else {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = contentPadding
                        ) {
                            items(state.iftaRows) { row ->
                                IftaStateRow(total = row)
                            }
                            item { IftaTotalsRow(rows = state.iftaRows) }
                            item { Spacer(Modifier.height(bottomPadding)) }
                        }
                    }
                }
                state.reportType == ReportType.GENERAL_MILEAGE -> {
                    if (state.generalMileageRows.isEmpty()) {
                        Box(modifier = Modifier.fillMaxSize()) {
                            Text(
                                text = "No mileage data for the current filters.",
                                modifier = Modifier.align(Alignment.Center)
                            )
                        }
                    } else {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = contentPadding
                        ) {
                            items(state.generalMileageRows) { row ->
                                GeneralMileageTruckRow(total = row)
                            }
                            item { GeneralMileageTotalsRow(rows = state.generalMileageRows) }
                            item { Spacer(Modifier.height(bottomPadding)) }
                        }
                    }
                }
                state.reportType == ReportType.REVENUE_PER_MILE -> {
                    if (state.revenuePerMile.rows.isEmpty()) {
                        Box(modifier = Modifier.fillMaxSize()) {
                            Text(
                                text = "No revenue or mileage for the current filters.",
                                modifier = Modifier.align(Alignment.Center)
                            )
                        }
                    } else {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = contentPadding
                        ) {
                            items(state.revenuePerMile.rows) { row ->
                                RevenuePerMileTruckRow(total = row)
                            }
                            item { RevenuePerMileTotalsRow(report = state.revenuePerMile) }
                            item { Spacer(Modifier.height(bottomPadding)) }
                        }
                    }
                }
                state.reportType == ReportType.LOADS_WITHOUT_TRUCK -> {
                    if (state.loadsWithoutTruckRows.isEmpty()) {
                        Box(modifier = Modifier.fillMaxSize()) {
                            Text(
                                text = "Every completed load has a truck.",
                                modifier = Modifier.align(Alignment.Center)
                            )
                        }
                    } else {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding = contentPadding
                        ) {
                            items(state.loadsWithoutTruckRows, key = { it.load.id }) { row ->
                                LoadWithoutTruckRow(
                                    row = row,
                                    onClick = { viewModel.onEvent(ReportingEvent.OnSetTruckClick(row)) },
                                )
                            }
                            item { Spacer(Modifier.height(bottomPadding)) }
                        }
                    }
                }
                state.missingStartEndRows.isEmpty() -> Box(modifier = Modifier.fillMaxSize()) {
                    Text(
                        text = "No missing start/end entries for the current filters.",
                        modifier = Modifier.align(Alignment.Center)
                    )
                }
                else -> LazyColumn(
                    modifier = Modifier.fillMaxSize(),
                    contentPadding = contentPadding
                ) {
                    items(state.missingStartEndRows) { row ->
                        MissingStartEndBoundaryRow(
                            row = row,
                            onClick = { viewModel.onEvent(ReportingEvent.OnMissingBoundaryClick(row)) },
                        )
                    }
                    item { Spacer(Modifier.height(bottomPadding)) }
                }
            }
        }
        }
    }

    state.fixDialog?.let { fix ->
        MissingStartEndFixDialog(fix = fix, onEvent = viewModel::onEvent)
    }

    state.setTruckDialog?.let { dialog ->
        SetTruckDialog(dialog = dialog, trucks = state.trucks, onEvent = viewModel::onEvent)
    }
}
